import { useLayoutEffect, useState, type CSSProperties, type RefObject } from 'react';

// Anchored positioning for the floating primitives (Popover, Menu, Tooltip, Explainer) without a positioning
// library: the floating element is `position: fixed`, placed against the anchor's viewport rectangle, flipped
// to the other side when it would overflow the viewport, and kept 8 px inside the edges. It follows the anchor
// on scroll (capture) and resize, and re-places itself when its OWN box changes size (BUG-165). jsdom gives every
// rect 0 × 0, so tests exercise behaviour, not geometry.

export type Placement = 'bottom-start' | 'bottom' | 'bottom-end' | 'top-start' | 'top' | 'top-end';

export interface PositionOptions {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  floatingRef: RefObject<HTMLElement | null>;
  placement?: Placement;
  /** Gap between anchor and floating element (px). */
  offset?: number;
}

export interface Position {
  style: CSSProperties;
  /** The side the element actually landed on, for the entrance transform origin. */
  side: 'top' | 'bottom';
}

const VIEWPORT_PADDING = 8;
const HIDDEN: Position = {
  style: { position: 'fixed', top: 0, left: 0, visibility: 'hidden' },
  side: 'bottom',
};

export function computePosition(
  anchor: DOMRect,
  floating: { width: number; height: number },
  viewport: { width: number; height: number },
  placement: Placement,
  offset: number,
): Position {
  const [requestedSide, align = 'center'] = placement.split('-') as [
    'top' | 'bottom',
    'start' | 'end' | undefined,
  ];
  const spaceBelow = viewport.height - anchor.bottom - offset;
  const spaceAbove = anchor.top - offset;
  let side = requestedSide;
  if (side === 'bottom' && floating.height > spaceBelow && spaceAbove > spaceBelow) side = 'top';
  if (side === 'top' && floating.height > spaceAbove && spaceBelow > spaceAbove) side = 'bottom';

  let left =
    align === 'start'
      ? anchor.left
      : align === 'end'
        ? anchor.right - floating.width
        : anchor.left + anchor.width / 2 - floating.width / 2;
  const maxLeft = Math.max(VIEWPORT_PADDING, viewport.width - floating.width - VIEWPORT_PADDING);
  left = Math.min(Math.max(left, VIEWPORT_PADDING), maxLeft);

  let top = side === 'bottom' ? anchor.bottom + offset : anchor.top - offset - floating.height;
  const maxTop = Math.max(VIEWPORT_PADDING, viewport.height - floating.height - VIEWPORT_PADDING);
  top = Math.min(Math.max(top, VIEWPORT_PADDING), maxTop);

  return {
    style: { position: 'fixed', top: Math.round(top), left: Math.round(left) },
    side,
  };
}

export function usePosition({
  open,
  anchorRef,
  floatingRef,
  placement = 'bottom-start',
  offset = 6,
}: PositionOptions): Position {
  const [position, setPosition] = useState<Position>(HIDDEN);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(HIDDEN);
      return;
    }
    const update = () => {
      const anchor = anchorRef.current;
      const floating = floatingRef.current;
      if (!anchor || !floating) return;
      const next = computePosition(
        anchor.getBoundingClientRect(),
        { width: floating.offsetWidth, height: floating.offsetHeight },
        { width: window.innerWidth, height: window.innerHeight },
        placement,
        offset,
      );
      setPosition((current) =>
        current.style.top === next.style.top &&
        current.style.left === next.style.left &&
        current.side === next.side &&
        current.style.visibility === undefined
          ? current
          : next,
      );
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    // BUG-165: the floating element is measured as it opens, and a dialog whose rows arrive later GROWS past that
    // measurement — the entity picker opened at 697 px with its footer 237 px below the fold, because the clamp
    // above ran on the skeleton. Re-place it whenever its own box changes size; jsdom has no ResizeObserver.
    const floating = floatingRef.current;
    let observer: ResizeObserver | null = null;
    if (floating && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => update());
      observer.observe(floating);
    }
    return () => {
      observer?.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, anchorRef, floatingRef, placement, offset]);

  return position;
}
